import { createUserWithEmailAndPassword, fetchSignInMethodsForEmail } from 'firebase/auth';
import { collection, addDoc, updateDoc, doc, getDoc, setDoc, serverTimestamp, query, where, limit, getDocs, deleteDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db, app } from '../firebase';
import { getClientPlanLimits } from '../constants/membershipPlans';
import { currentMonth } from '../features/cliente/services/ClientPlanService';

/**
 * Servicio para crear usuarios en Firebase Authentication desde el admin
 * cuando se aprueban solicitudes de cliente
 */

export class UserCreationService {
  
  /**
   * Crear usuario de cliente a partir de una solicitud aprobada
   */
  static async createClientUser(solicitudData) {
    try {
      console.log('🔵 Iniciando creación de usuario cliente...');
      console.log('📋 Datos de solicitud recibidos:', {
        email: solicitudData.email,
        nombres: solicitudData.nombres,
        apellidos: solicitudData.apellidos,
        tienePasswordHash: !!solicitudData.password_hash
      });

      // Extraer datos de la solicitud
      const {
        nombres,
        apellidos,
        email,
        password_hash,
        telefono,
        direccion,
        comuna,
        region,
        rut,
        fecha_nacimiento,
        genero
      } = solicitudData;

      // Validar campos requeridos
      if (!email) {
        console.error('❌ Email es requerido');
        return {
          success: false,
          error: 'El email es requerido para crear el usuario.',
          code: 'missing_email'
        };
      }

      if (!nombres || !apellidos) {
        console.error('❌ Nombres y apellidos son requeridos');
        return {
          success: false,
          error: 'Los nombres y apellidos son requeridos para crear el usuario.',
          code: 'missing_name'
        };
      }

      // Validar que exista password_hash o generar uno temporal
      let passwordToUse = password_hash;
      let isTemporaryPassword = false;
      
      if (!password_hash) {
        console.warn('⚠️ password_hash no encontrado en solicitud. Generando contraseña temporal.');
        
        // Generar una contraseña temporal segura
        // Formato: Cliente + primeros 4 caracteres del email + año actual + símbolo
        const emailPrefix = email ? email.split('@')[0].substring(0, 4).toUpperCase() : 'CLI';
        const year = new Date().getFullYear();
        const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        const tempPassword = `Cliente${emailPrefix}${year}${randomNum}!`;
        
        // Codificar la contraseña temporal en base64
        passwordToUse = btoa(tempPassword);
        isTemporaryPassword = true;
        
        console.log('🔑 Contraseña temporal generada para:', email);
      }

      // Validar que passwordToUse sea una cadena
      if (typeof passwordToUse !== 'string') {
        console.error('❌ password_hash no es una cadena:', typeof passwordToUse, passwordToUse);
        return {
          success: false,
          error: 'El password_hash no es válido. Debe ser una cadena codificada en base64.',
          code: 'invalid_password_hash_type'
        };
      }

      // Validar que passwordToUse no esté vacío
      if (passwordToUse.trim() === '') {
        console.error('❌ password_hash está vacío');
        return {
          success: false,
          error: 'El password_hash está vacío.',
          code: 'empty_password_hash'
        };
      }

      // Limpiar password_hash (eliminar espacios y caracteres no válidos)
      const passwordHashClean = passwordToUse.trim().replace(/\s/g, '');
      
      // Validar formato base64 básico (solo caracteres válidos)
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(passwordHashClean)) {
        console.error('❌ password_hash no tiene formato base64 válido');
        console.error('❌ Primeros 50 caracteres:', passwordHashClean.substring(0, 50));
        console.error('❌ Tipo:', typeof passwordHashClean);
        console.error('❌ Longitud:', passwordHashClean.length);
        return {
          success: false,
          error: 'El password_hash no tiene un formato base64 válido. Contiene caracteres no permitidos.',
          code: 'invalid_base64_format'
        };
      }

      // Validar longitud mínima de base64 (al menos 4 caracteres para una contraseña mínima)
      if (passwordHashClean.length < 4) {
        console.error('❌ password_hash demasiado corto:', passwordHashClean.length);
        return {
          success: false,
          error: 'El password_hash es demasiado corto para ser válido.',
          code: 'password_hash_too_short'
        };
      }

      // Decodificar la contraseña (recordar que está en base64)
      let password;
      try {
        // Intentar decodificar
        password = atob(passwordHashClean);
        
        // Validar que la decodificación fue exitosa
        if (!password || typeof password !== 'string') {
          console.error('❌ Contraseña decodificada no es válida');
          return {
            success: false,
            error: 'La contraseña decodificada no es válida.',
            code: 'invalid_decoded_password'
          };
        }
        
        // Validar que no esté vacía
        if (password.trim() === '') {
          console.error('❌ Contraseña decodificada está vacía');
          return {
            success: false,
            error: 'La contraseña decodificada está vacía.',
            code: 'empty_decoded_password'
          };
        }
        
        // Validar longitud mínima de contraseña
        if (password.length < 6) {
          console.error('❌ Contraseña decodificada es demasiado corta:', password.length);
          return {
            success: false,
            error: 'La contraseña decodificada es demasiado corta (mínimo 6 caracteres).',
            code: 'password_too_short'
          };
        }
      } catch (decodeError) {
        console.error('❌ Error decodificando contraseña:', decodeError);
        console.error('❌ Tipo de error:', decodeError.name);
        console.error('❌ Mensaje:', decodeError.message);
        console.error('❌ password_hash (primeros 50 chars):', passwordHashClean.substring(0, 50));
        console.error('❌ password_hash (longitud):', passwordHashClean.length);
        
        return {
          success: false,
          error: `Error al decodificar la contraseña: ${decodeError.message}. El password_hash puede estar corrupto o no ser base64 válido.`,
          code: 'decode_error',
          details: {
            errorName: decodeError.name,
            errorMessage: decodeError.message,
            hashLength: passwordHashClean.length,
            hashPreview: passwordHashClean.substring(0, 20)
          }
        };
      }

      // Validar email
      if (!email || typeof email !== 'string' || !email.includes('@')) {
        return {
          success: false,
          error: 'El email no es válido.',
          code: 'invalid_email'
        };
      }

      console.log('🔐 Creando usuario en Firebase Auth:', { email, passwordLength: password.length });

      // Verificar si el usuario ya existe en Firebase Auth antes de intentar crearlo
      let user;
      let usuarioYaExistia = false;
      
      try {
        // Primero verificar si el usuario existe en Firebase Auth
        const authCheck = await this.checkUserExistsInAuth(email);
        
        if (authCheck.exists) {
          console.log('⚠️ El usuario ya existe en Firebase Auth. Obteniendo información...');
          usuarioYaExistia = true;
          
          // Buscar el usuario en la colección usuarios para obtener su UID
          const usuariosQuery = query(
            collection(db, 'usuarios'),
            where('email', '==', email),
            limit(1)
          );
          const usuariosSnapshot = await getDocs(usuariosQuery);
          
          if (!usuariosSnapshot.empty) {
            const usuarioDoc = usuariosSnapshot.docs[0];
            const usuarioData = usuarioDoc.data();
            const uidExistente = usuarioData.uid || usuarioDoc.id;
            
            // Crear un objeto user simulado con el UID existente
            user = {
              uid: uidExistente,
              email: email
            };
            
            console.log('✅ Usuario existente encontrado, UID:', uidExistente);
            // Continuar con el flujo normal (no intentar crear usuario)
            } else {
              // Si no está en usuarios pero existe en Auth, intentar obtener el UID usando Cloud Function
              console.log('⚠️ Usuario existe en Auth pero no en usuarios. Intentando obtener UID...');
              
              // Intentar usar Cloud Function para obtener el UID del usuario
              try {
                const functions = getFunctions();
                const obtenerUidUsuario = httpsCallable(functions, 'obtenerUidUsuarioPorEmail');
                const resultado = await obtenerUidUsuario({ email });
                
                if (resultado.data && resultado.data.uid) {
                  const uidExistente = resultado.data.uid;
                  user = {
                    uid: uidExistente,
                    email: email
                  };
                  usuarioYaExistia = true;
                  console.log('✅ UID obtenido de Cloud Function:', uidExistente);
                  // Continuar con el flujo normal
                } else {
                  throw new Error('No se pudo obtener el UID del usuario');
                }
              } catch (cfError) {
                console.log('⚠️ Cloud Function no disponible o falló. Intentando método alternativo...');
                
                // Método alternativo: Intentar usar fetchSignInMethodsForEmail para confirmar que existe
                try {
                  const methods = await fetchSignInMethodsForEmail(auth, email);
                  if (methods.length > 0) {
                    console.log('⚠️ Usuario confirmado en Auth pero no se puede obtener UID. Se creará registro en usuarios con UID temporal.');
                    // Generar un UID temporal basado en el email (hash simple)
                    // Esto permitirá crear el registro en usuarios y continuar con el proceso
                    // El admin deberá vincular manualmente el UID real más tarde
                    const uidTemporal = `temp_${btoa(email).replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)}`;
                    
                    user = {
                      uid: uidTemporal,
                      email: email
                    };
                    usuarioYaExistia = true;
                    console.log('⚠️ Usando UID temporal:', uidTemporal);
                    console.log('⚠️ IMPORTANTE: El admin debe vincular el UID real de Firebase Auth más tarde.');
                    // Continuar con el flujo normal pero marcar que necesita vinculación
                  } else {
                    throw new Error('No se pudo confirmar que el usuario existe en Auth');
                  }
                } catch (fetchError) {
                  console.error('❌ Error verificando métodos de autenticación:', fetchError);
                  return {
                    success: false,
                    error: 'El usuario existe en Firebase Auth pero no se encontró en la base de datos y no se pudo obtener el UID. Por favor, contacta al administrador para vincular el usuario manualmente.',
                    code: 'auth/email-already-in-use',
                    emailExists: true,
                    usuarioExisteEnAuth: true
                  };
                }
              }
            }
        } else {
          // El usuario no existe, crear uno nuevo
          console.log('🔐 Creando nuevo usuario en Firebase Auth...');
          try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            user = userCredential.user;
            console.log('✅ Nuevo usuario creado en Firebase Auth:', user.uid);
          } catch (createError) {
            // Si falla al crear, puede ser que el usuario se creó entre la verificación y el intento de creación
            if (createError.code === 'auth/email-already-in-use') {
              console.log('⚠️ Usuario creado entre verificación e intento de creación. Buscando usuario existente...');
              // Lanzar el error para que sea manejado por el catch externo
              throw createError;
            } else {
              throw createError;
            }
          }
        }
      } catch (authError) {
        console.error('❌ Error en proceso de autenticación:', authError);
        console.error('❌ Código de error:', authError.code);
        console.error('❌ Mensaje de error:', authError.message);
        
        // Si el error es que el email ya está en uso, intentar obtener el UID del usuario existente
        if (authError.code === 'auth/email-already-in-use' || authError.message?.includes('email-already-in-use')) {
          console.log('⚠️ Email ya en uso. Buscando usuario existente...');
          
          try {
            // Buscar el usuario en la colección usuarios
            const usuariosQuery = query(
              collection(db, 'usuarios'),
              where('email', '==', email),
              limit(1)
            );
            const usuariosSnapshot = await getDocs(usuariosQuery);
            
            if (!usuariosSnapshot.empty) {
              const usuarioDoc = usuariosSnapshot.docs[0];
              const usuarioData = usuarioDoc.data();
              const uidExistente = usuarioData.uid || usuarioDoc.id;
              
              user = {
                uid: uidExistente,
                email: email
              };
              
              usuarioYaExistia = true;
              console.log('✅ Usuario existente encontrado después del error, UID:', uidExistente);
              // Continuar con el flujo normal (no retornar error, continuar con la creación/actualización del perfil)
            } else {
              // Si no se encuentra en usuarios, intentar obtener el UID usando Cloud Function
              console.log('⚠️ No se encontró en usuarios. Intentando obtener UID de Firebase Auth...');
              try {
                // Intentar usar Cloud Function para obtener el UID
                const functions = getFunctions();
                const obtenerUidUsuario = httpsCallable(functions, 'obtenerUidUsuarioPorEmail');
                const resultado = await obtenerUidUsuario({ email });
                
                if (resultado.data && resultado.data.uid) {
                  const uidExistente = resultado.data.uid;
                  user = {
                    uid: uidExistente,
                    email: email
                  };
                  usuarioYaExistia = true;
                  console.log('✅ UID obtenido de Cloud Function después del error:', uidExistente);
                  // Continuar con el flujo normal
                } else {
                  throw new Error('No se pudo obtener el UID del usuario');
                }
              } catch (cfError) {
                console.log('⚠️ Cloud Function no disponible. Intentando método alternativo...');
                
                // Método alternativo: usar fetchSignInMethodsForEmail
                try {
                  const methods = await fetchSignInMethodsForEmail(auth, email);
                  if (methods.length > 0) {
                    console.log('⚠️ Usuario existe en Auth pero no se puede obtener UID. Usando UID temporal.');
                    // Generar un UID temporal
                    const uidTemporal = `temp_${btoa(email).replace(/[^a-zA-Z0-9]/g, '').substring(0, 20)}`;
                    
                    user = {
                      uid: uidTemporal,
                      email: email
                    };
                    usuarioYaExistia = true;
                    console.log('⚠️ Usando UID temporal:', uidTemporal);
                    // Continuar con el flujo normal
                  } else {
                    return {
                      success: false,
                      error: 'El email ya está registrado en Firebase Auth pero no se encontró en la base de datos. Contacta al administrador.',
                      code: 'auth/email-already-in-use',
                      emailExists: true
                    };
                  }
                } catch (fetchError) {
                  console.error('❌ Error obteniendo métodos de autenticación:', fetchError);
                  return {
                    success: false,
                    error: 'El email ya está registrado en Firebase Auth pero no se encontró en la base de datos. Contacta al administrador.',
                    code: 'auth/email-already-in-use',
                    emailExists: true
                  };
                }
              }
            }
          } catch (searchError) {
            console.error('❌ Error buscando usuario existente:', searchError);
            return {
              success: false,
              error: `El email ya está registrado en Firebase Auth. Error al buscar usuario: ${searchError.message}`,
              code: 'auth/email-already-in-use',
              emailExists: true
            };
          }
        } else {
          return {
            success: false,
            error: `Error al crear usuario en Firebase Auth: ${authError.message}`,
            code: authError.code || 'auth_error'
          };
        }
      }
      
      console.log('✅ Usuario listo para procesar:', { uid: user.uid, email: user.email, usuarioYaExistia });

      // Función helper para validar y crear fechas válidas
      const crearFechaValida = (valor) => {
        if (!valor) return null;
        try {
          // Si es un Timestamp de Firestore, convertir a Date
          if (valor && typeof valor === 'object' && 'toDate' in valor && typeof valor.toDate === 'function') {
            try {
              const fecha = valor.toDate();
              if (isNaN(fecha.getTime())) {
                console.warn('⚠️ Fecha inválida desde Timestamp:', valor);
                return null;
              }
              return fecha;
            } catch (toDateError) {
              console.warn('⚠️ Error llamando toDate() en Timestamp:', toDateError);
              return null;
            }
          }
          
          // Si es un objeto Date, validar que sea válido
          if (valor instanceof Date) {
            if (isNaN(valor.getTime())) {
              console.warn('⚠️ Fecha Date inválida:', valor);
              return null;
            }
            return valor;
          }
          
          // Si es string o number, intentar crear Date
          if (typeof valor === 'string' || typeof valor === 'number') {
            const fecha = new Date(valor);
            if (isNaN(fecha.getTime())) {
              console.warn('⚠️ Fecha inválida (string/number):', valor);
              return null;
            }
            return fecha;
          }
          
          // Si no es ninguno de los tipos esperados, retornar null
          console.warn('⚠️ Tipo de fecha no reconocido:', typeof valor, valor);
          return null;
        } catch (error) {
          console.warn('⚠️ Error parseando fecha:', valor, error);
          return null;
        }
      };

      // Crear perfil de cliente en Firestore
      const perfilClienteData = {
        uid: user.uid,
        email: user.email,
        nombres,
        apellidos,
        telefono,
        direccion,
        comuna,
        region,
        rut,
        fecha_nacimiento: crearFechaValida(fecha_nacimiento),
        genero: genero || '',
        
        // Información de perfil
        userId: user.uid, // Para compatibilidad con DashboardCliente
        fecha_registro: serverTimestamp(), // Usar serverTimestamp para consistencia
        fechaRegistro: serverTimestamp(), // Para compatibilidad con AdminValidacionClientes
        estado: 'pendiente_validacion', // Campo consistente usado en toda la app
        tipo_usuario: 'cliente',
        fecha_activacion: serverTimestamp(),
        
        // Configuraciones por defecto
        notificaciones_email: true,
        notificaciones_push: true,
        acepta_promociones: true,
        
        // Vehiculos (array vacío inicialmente)
        vehiculos: [],
        
        // Estadísticas
        total_solicitudes: 0,
        total_servicios_completados: 0,
        calificacion_promedio: null,
        
        // Metadatos
        creado_desde_solicitud: true,
        solicitud_original_id: solicitudData.id || null,
        ip_registro: solicitudData.ip_registro || null,
        user_agent: solicitudData.user_agent || null
      };

      // Verificar si el perfil ya existe antes de crearlo
      let docRef;
      let perfilYaExistia = false;
      
      try {
        if (usuarioYaExistia && user.uid) {
          // Buscar si ya existe un perfil para este usuario
          const perfilesQuery = query(
            collection(db, 'perfiles_clientes'),
            where('uid', '==', user.uid),
            limit(1)
          );
          const perfilesSnapshot = await getDocs(perfilesQuery);
          
          if (!perfilesSnapshot.empty) {
            perfilYaExistia = true;
            docRef = { id: perfilesSnapshot.docs[0].id };
            console.log('✅ Perfil de cliente ya existe, ID:', docRef.id);
            
            // Actualizar el perfil existente con los datos más recientes
            // IMPORTANTE: No incluir campos con serverTimestamp() en updateDoc
            // Solo actualizar campos que no sean timestamps del servidor
            const datosActualizacion = {
              uid: perfilClienteData.uid,
              email: perfilClienteData.email,
              nombres: perfilClienteData.nombres,
              apellidos: perfilClienteData.apellidos,
              telefono: perfilClienteData.telefono || null,
              direccion: perfilClienteData.direccion || null,
              comuna: perfilClienteData.comuna || null,
              region: perfilClienteData.region || null,
              rut: perfilClienteData.rut || null,
              genero: perfilClienteData.genero || '',
              userId: perfilClienteData.userId,
              estado: perfilClienteData.estado,
              tipo_usuario: perfilClienteData.tipo_usuario,
              notificaciones_email: perfilClienteData.notificaciones_email !== undefined ? perfilClienteData.notificaciones_email : true,
              notificaciones_push: perfilClienteData.notificaciones_push !== undefined ? perfilClienteData.notificaciones_push : true,
              acepta_promociones: perfilClienteData.acepta_promociones !== undefined ? perfilClienteData.acepta_promociones : true,
              fecha_actualizacion: serverTimestamp(),
              actualizado_por_admin: true
            };
            
            // Solo agregar fecha_nacimiento si es válida (Date válido)
            if (perfilClienteData.fecha_nacimiento) {
              if (perfilClienteData.fecha_nacimiento instanceof Date && !isNaN(perfilClienteData.fecha_nacimiento.getTime())) {
                datosActualizacion.fecha_nacimiento = perfilClienteData.fecha_nacimiento;
                console.log('✅ fecha_nacimiento válida agregada:', perfilClienteData.fecha_nacimiento);
              } else {
                console.warn('⚠️ fecha_nacimiento no es una Date válida, omitiendo del update:', perfilClienteData.fecha_nacimiento, typeof perfilClienteData.fecha_nacimiento);
              }
            } else {
              console.log('ℹ️ fecha_nacimiento es null/undefined, no se incluirá en el update');
            }
            
            // NO incluir fecha_registro, fechaRegistro, fecha_activacion en updateDoc
            // porque contienen serverTimestamp() que no se puede usar en updates
            
            // Validar que no haya campos Date inválidos antes de hacer updateDoc
            const camposInvalidos = [];
            Object.keys(datosActualizacion).forEach(key => {
              const value = datosActualizacion[key];
              if (value instanceof Date) {
                if (isNaN(value.getTime())) {
                  camposInvalidos.push(key);
                  console.error(`❌ Campo ${key} tiene una Date inválida:`, value);
                }
              }
            });
            
            if (camposInvalidos.length > 0) {
              console.error('❌ Campos con fechas inválidas encontrados:', camposInvalidos);
              // Remover campos inválidos
              camposInvalidos.forEach(key => {
                delete datosActualizacion[key];
                console.warn(`⚠️ Removido campo inválido ${key} del update`);
              });
            }
            
            console.log('📝 Actualizando perfil con datos:', Object.keys(datosActualizacion));
            console.log('📝 Valores de datosActualizacion:', JSON.stringify(datosActualizacion, (key, value) => {
              if (value instanceof Date) {
                return value.toISOString();
              }
              return value;
            }, 2));
            
            await updateDoc(doc(db, 'perfiles_clientes', docRef.id), datosActualizacion);
          } else {
            // Crear nuevo perfil
            docRef = await addDoc(collection(db, 'perfiles_clientes'), perfilClienteData);
            console.log('✅ Nuevo perfil de cliente creado, ID:', docRef.id);
          }
        } else {
          // Crear nuevo perfil
          docRef = await addDoc(collection(db, 'perfiles_clientes'), perfilClienteData);
          console.log('✅ Nuevo perfil de cliente creado, ID:', docRef.id);
        }
      } catch (perfilError) {
        console.error('❌ Error creando/actualizando perfil:', perfilError);
        throw perfilError;
      }

      // Crear registro en la colección usuarios para asignar el rol
      // IMPORTANTE: Usar serverTimestamp() para fechaCreacion para consistencia
      // Normalizar nombre a mayúsculas para consistencia
      const nombreCompleto = `${nombres.trim()} ${apellidos.trim()}`.trim().toUpperCase();
      
      // Verificar si se está usando un UID temporal
      const uidEsTemporal = user.uid && user.uid.startsWith('temp_');
      
      const planLimits = getClientPlanLimits('basic');
      const usuarioData = {
        uid: user.uid,
        email: user.email.toLowerCase().trim(), // Normalizar email
        rol: 'cliente',
        nombre: nombreCompleto,
        fechaCreacion: serverTimestamp(), // Usar serverTimestamp para consistencia
        activo: true, // Boolean explícito
        perfilClienteId: docRef.id,
        plan: 'basic',
        limits: planLimits,
        usage: { plateChecks: { month: currentMonth(), used: 0 } },
        updatedAt: serverTimestamp()
      };
      
      // Si se usa un UID temporal, agregar advertencia
      if (uidEsTemporal) {
        usuarioData.uid_temporal = true;
        usuarioData.nota_uid_temporal = 'Este UID es temporal. El usuario existe en Firebase Auth pero no se pudo obtener el UID real. El admin debe vincular el UID real manualmente.';
        console.warn('⚠️ Se está usando un UID temporal. El admin debe vincular el UID real de Firebase Auth más tarde.');
      }
      
      console.log('📝 Datos del usuario a crear en colección usuarios:', usuarioData);
      
      // Verificar si el usuario ya existe en la colección usuarios
      const usuarioDoc = await getDoc(doc(db, 'usuarios', user.uid));
      
      if (usuarioDoc.exists()) {
        console.log('⚠️ Usuario ya existe en colección usuarios. Actualizando datos...');
        // Actualizar datos existentes
        await updateDoc(doc(db, 'usuarios', user.uid), {
          ...usuarioData,
          fechaActualizacion: serverTimestamp(),
          actualizadoPorAdmin: true
        });
        console.log('✅ Usuario actualizado en colección usuarios');
      } else {
        // Crear nuevo registro
        await setDoc(doc(db, 'usuarios', user.uid), usuarioData);
        console.log('✅ Nuevo usuario creado en colección usuarios');
      }
      
      console.log('✅ setDoc/updateDoc ejecutado para usuarios/', user.uid);
      
      console.log('✅ Usuario creado en Firestore (usuarios):', {
        uid: user.uid,
        email: user.email,
        rol: 'cliente',
        activo: true,
        perfilClienteId: docRef.id,
        nombre: usuarioData.nombre
      });

      // Verificar que el usuario se creó correctamente
      const usuarioVerificado = await getDoc(doc(db, 'usuarios', user.uid));
      if (!usuarioVerificado.exists()) {
        console.error('❌ El usuario no se creó en Firestore después de setDoc');
        throw new Error('Error: El usuario no se creó correctamente en Firestore');
      }
      
      const datosVerificados = usuarioVerificado.data();
      console.log('✅ Verificación exitosa: Usuario existe en Firestore');
      console.log('📋 Datos verificados:', {
        uid: datosVerificados.uid,
        email: datosVerificados.email,
        rol: datosVerificados.rol,
        nombre: datosVerificados.nombre,
        activo: datosVerificados.activo,
        perfilClienteId: datosVerificados.perfilClienteId,
        fechaCreacion: datosVerificados.fechaCreacion ? 'presente' : 'ausente'
      });

      return {
        success: true,
        user: user,
        profileId: docRef.id,
        uid: user.uid,
        email: user.email,
        isTemporaryPassword: isTemporaryPassword,
        temporaryPassword: isTemporaryPassword ? password : null,
        uidEsTemporal: uidEsTemporal || false,
        usuarioYaExistia: usuarioYaExistia || false,
        usuarioYaExistia: usuarioYaExistia,
        perfilYaExistia: perfilYaExistia
      };

    } catch (error) {
      console.error('Error creating client user:', error);
      
      let errorMessage = 'Error al crear el usuario';
      
      // Manejar errores específicos de Firebase
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'El email ya está registrado en Firebase Auth. El usuario puede intentar iniciar sesión o restablecer su contraseña.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'La contraseña es muy débil';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'El email no es válido';
      }

      return {
        success: false,
        error: errorMessage,
        code: error.code
      };
    }
  }

  /**
   * Verificar si un usuario existe en Firebase Auth
   */
  static async checkUserExistsInAuth(email) {
    try {
      if (!email || typeof email !== 'string' || !email.includes('@')) {
        console.warn('⚠️ Email inválido para verificación:', email);
        return { exists: false, methods: [] };
      }
      
      console.log('🔍 Verificando existencia de usuario en Firebase Auth:', email);
      const methods = await fetchSignInMethodsForEmail(auth, email);
      const exists = methods.length > 0;
      
      console.log('📋 Resultado verificación:', { 
        email, 
        exists, 
        methodsCount: methods.length, 
        methods: methods 
      });
      
      return {
        exists: exists,
        methods: methods
      };
    } catch (error) {
      console.error('❌ Error en checkUserExistsInAuth:', error);
      console.error('❌ Código de error:', error.code);
      console.error('❌ Mensaje de error:', error.message);
      
      // Si el error es que el email no existe, retornar false
      if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-email') {
        console.log('⚠️ Usuario no encontrado en Firebase Auth (error esperado):', email);
        return { exists: false, methods: [] };
      }
      
      // Si el error es que el email ya está en uso (caso raro pero posible), retornar true
      if (error.code === 'auth/email-already-in-use') {
        console.log('⚠️ Email ya en uso detectado en verificación:', email);
        return { exists: true, methods: ['password'] };
      }
      
      // Para otros errores, loguear y retornar false por seguridad
      console.error('⚠️ Error inesperado verificando usuario en Firebase Auth:', error);
      return { exists: false, methods: [], error: error.message, errorCode: error.code };
    }
  }

  /**
   * Recrear usuario en Firebase Auth si existe en Firestore pero no en Auth
   */
  static async recreateUserInAuth(solicitudData) {
    try {
      const { email, password_hash } = solicitudData;

      if (!password_hash) {
        return {
          success: false,
          error: 'No se encontró password_hash en la solicitud'
        };
      }

      // Verificar si el usuario ya existe en Auth ANTES de intentar crearlo
      console.log('🔍 Verificando si el usuario ya existe en Firebase Auth...');
      let authCheck;
      try {
        authCheck = await this.checkUserExistsInAuth(email);
        console.log('📋 Resultado de verificación:', { exists: authCheck.exists, methods: authCheck.methods });
      } catch (checkError) {
        console.error('❌ Error verificando usuario en Auth:', checkError);
        // Continuar con el proceso aunque falle la verificación
        authCheck = { exists: false, methods: [] };
      }
      
      if (authCheck.exists) {
        console.log('⚠️ El usuario ya existe en Firebase Auth. Obteniendo información...');
        
        // Buscar el usuario en la colección usuarios para obtener su UID
        try {
          const usuariosQuery = query(
            collection(db, 'usuarios'),
            where('email', '==', email),
            limit(1)
          );
          const usuariosSnapshot = await getDocs(usuariosQuery);
          
          if (!usuariosSnapshot.empty) {
            const usuarioDoc = usuariosSnapshot.docs[0];
            const usuarioData = usuarioDoc.data();
            const uidExistente = usuarioData.uid || usuarioDoc.id;
            
            console.log('✅ Usuario existente encontrado, UID:', uidExistente);
            
            return {
              success: true,
              uid: uidExistente,
              email: email,
              usuarioYaExistia: true,
              message: 'El usuario ya existe en Firebase Auth. Se actualizó la información.'
            };
          } else {
            // Si existe en Auth pero no en usuarios, retornar error informativo
            console.warn('⚠️ Usuario existe en Auth pero no en usuarios');
            return {
              success: false,
              error: 'El usuario ya existe en Firebase Auth pero no se encontró en la base de datos. Contacta al administrador.',
              code: 'auth/email-already-in-use',
              emailExists: true
            };
          }
        } catch (searchError) {
          console.error('❌ Error buscando usuario en usuarios:', searchError);
          return {
            success: false,
            error: 'El usuario ya existe en Firebase Auth pero hubo un error al buscar en la base de datos.',
            code: 'auth/email-already-in-use',
            emailExists: true
          };
        }
      }
      
      console.log('✅ Usuario no existe en Firebase Auth. Procediendo a crear...');

      // Validar password_hash
      if (typeof password_hash !== 'string' || password_hash.trim() === '') {
        return {
          success: false,
          error: 'El password_hash no es válido o está vacío.',
          code: 'invalid_password_hash'
        };
      }

      // Limpiar password_hash
      const passwordHashClean = password_hash.trim().replace(/\s/g, '');

      // Validar formato base64
      const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
      if (!base64Regex.test(passwordHashClean)) {
        return {
          success: false,
          error: 'El password_hash no tiene un formato base64 válido.',
          code: 'invalid_base64_format'
        };
      }

      // Decodificar la contraseña
      let password;
      try {
        password = atob(passwordHashClean);
        if (!password || typeof password !== 'string' || password.trim() === '') {
          return {
            success: false,
            error: 'La contraseña decodificada está vacía.',
            code: 'empty_decoded_password'
          };
        }
      } catch (decodeError) {
        console.error('❌ Error decodificando contraseña (recrear):', decodeError);
        return {
          success: false,
          error: `Error al decodificar la contraseña: ${decodeError.message}`,
          code: 'decode_error'
        };
      }

      console.log('🔄 Recreando usuario en Firebase Auth:', { email });

      // Crear usuario en Firebase Auth
      let user;
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        user = userCredential.user;
        console.log('✅ Usuario recreado en Firebase Auth:', { uid: user.uid, email: user.email });
      } catch (authError) {
        console.error('❌ Error creando usuario en Firebase Auth:', authError);
        
        // Si el error es que el email ya está en uso, eliminar y recrear
        if (authError.code === 'auth/email-already-in-use') {
          console.log('⚠️ Email ya en uso. Intentando eliminar y recrear usuario...');
          
          try {
            // Verificar que db esté inicializado
            if (!db) {
              console.error('❌ db no está inicializado');
              throw new Error('Firestore no está inicializado');
            }
            
            // Intentar eliminar el usuario existente usando una Cloud Function
            // Si no hay Cloud Function disponible, intentar obtener el UID y continuar
            const usuariosQuery = query(
              collection(db, 'usuarios'),
              where('email', '==', email),
              limit(1)
            );
            const usuariosSnapshot = await getDocs(usuariosQuery);
            
            let uidExistente = null;
            if (!usuariosSnapshot.empty) {
              const usuarioDoc = usuariosSnapshot.docs[0];
              const usuarioData = usuarioDoc.data();
              uidExistente = usuarioData.uid || usuarioDoc.id;
              console.log('✅ Usuario existente encontrado, UID:', uidExistente);
            }
            
            // Intentar eliminar el usuario usando Cloud Function
            let usuarioEliminado = false;
            try {
              // Obtener la app de Firebase para usar con getFunctions
              let firebaseApp = app;
              if (!firebaseApp) {
                try {
                  const firebaseModule = await import('../firebase');
                  firebaseApp = firebaseModule.app || firebaseModule.default?.app;
                } catch (importError) {
                  console.warn('⚠️ No se pudo importar app de Firebase:', importError);
                }
              }
              
              if (firebaseApp) {
                const functions = getFunctions(firebaseApp);
                const deleteFunction = httpsCallable(functions, 'deleteUserByEmail');
                const deleteResult = await deleteFunction({ email });
                console.log('✅ Usuario eliminado mediante Cloud Function:', deleteResult.data);
                usuarioEliminado = true;
                
                // Esperar un momento para que Firebase procese la eliminación
                await new Promise(resolve => setTimeout(resolve, 2000));
              } else {
                throw new Error('No se pudo obtener la app de Firebase');
              }
              
            } catch (deleteError) {
              console.warn('⚠️ No se pudo eliminar mediante Cloud Function:', deleteError);
              console.log('ℹ️ La Cloud Function puede no estar configurada. Intentando método alternativo...');
              
              // Si no hay Cloud Function, intentar eliminar documentos de Firestore y mostrar mensaje
              try {
                // Verificar que db esté inicializado
                if (!db) {
                  console.error('❌ db no está inicializado para eliminar documentos');
                  throw new Error('Firestore no está inicializado');
                }
                
                // Eliminar documentos relacionados en Firestore
                if (uidExistente) {
                  // Eliminar de usuarios
                  const usuariosQueryDelete = query(
                    collection(db, 'usuarios'),
                    where('email', '==', email),
                    limit(1)
                  );
                  const usuariosSnapshotDelete = await getDocs(usuariosQueryDelete);
                  if (!usuariosSnapshotDelete.empty) {
                    await deleteDoc(doc(db, 'usuarios', usuariosSnapshotDelete.docs[0].id));
                    console.log('🗑️ Documento eliminado de usuarios');
                  }
                  
                  // Eliminar de perfiles_clientes
                  const perfilesQueryDelete = query(
                    collection(db, 'perfiles_clientes'),
                    where('email', '==', email),
                    limit(1)
                  );
                  const perfilesSnapshotDelete = await getDocs(perfilesQueryDelete);
                  if (!perfilesSnapshotDelete.empty) {
                    await deleteDoc(doc(db, 'perfiles_clientes', perfilesSnapshotDelete.docs[0].id));
                    console.log('🗑️ Perfil eliminado de perfiles_clientes');
                  }
                  
                  console.log('⚠️ Documentos de Firestore eliminados, pero el usuario en Firebase Auth debe eliminarse manualmente desde Firebase Console.');
                }
              } catch (firestoreDeleteError) {
                console.error('❌ Error eliminando documentos de Firestore:', firestoreDeleteError);
              }
            }
            
            // Si se eliminó el usuario (mediante Cloud Function), intentar recrearlo
            if (usuarioEliminado) {
              try {
                console.log('🔄 Reintentando crear usuario después de eliminación...');
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                user = userCredential.user;
                console.log('✅ Usuario recreado exitosamente:', { uid: user.uid, email: user.email });
              } catch (recreateError) {
                console.error('❌ Error recreando usuario después de eliminación:', recreateError);
                // Si aún falla, puede ser que el usuario no se haya eliminado completamente
                if (recreateError.code === 'auth/email-already-in-use') {
                  return {
                    success: false,
                    error: 'El usuario aún existe en Firebase Auth después del intento de eliminación. Por favor, elimina el usuario manualmente desde Firebase Console y vuelve a intentar.',
                    code: 'auth/email-already-in-use',
                    emailExists: true,
                    requiresManualDeletion: true
                  };
                } else {
                  throw recreateError;
                }
              }
            } else {
              // Si no se pudo eliminar, retornar el usuario existente o error
              if (uidExistente) {
                console.log('✅ Usando usuario existente encontrado, UID:', uidExistente);
                return {
                  success: true,
                  uid: uidExistente,
                  email: email,
                  usuarioYaExistia: true,
                  message: 'El usuario ya existía en Firebase Auth. Se actualizó la información. Para recrearlo completamente, elimina el usuario manualmente desde Firebase Console.',
                  warning: 'No se pudo eliminar automáticamente. El usuario existe en Firebase Auth.'
                };
              } else {
                return {
                  success: false,
                  error: 'El email ya está registrado en Firebase Auth pero no se encontró en la base de datos. Por favor, elimina el usuario manualmente desde Firebase Console y vuelve a intentar.',
                  code: 'auth/email-already-in-use',
                  emailExists: true,
                  requiresManualDeletion: true
                };
              }
            }
          } catch (searchError) {
            console.error('❌ Error buscando o eliminando usuario:', searchError);
            return {
              success: false,
              error: 'El email ya está registrado en Firebase Auth. Error al procesar: ' + searchError.message,
              code: 'auth/email-already-in-use',
              emailExists: true
            };
          }
        } else {
          throw authError;
        }
      }

      // Buscar el documento en usuarios por email para actualizar el UID si es necesario
      const usuariosQuery = query(
        collection(db, 'usuarios'),
        where('email', '==', email),
        limit(1)
      );
      const usuariosSnapshot = await getDocs(usuariosQuery);
      
      if (!usuariosSnapshot.empty) {
        const usuarioDoc = usuariosSnapshot.docs[0];
        await updateDoc(doc(db, 'usuarios', usuarioDoc.id), {
          uid: user.uid
        });
        console.log('✅ UID actualizado en Firestore');
      }

      return {
        success: true,
        uid: user.uid,
        email: user.email,
        usuarioYaExistia: false,
        message: 'Usuario recreado exitosamente en Firebase Auth.'
      };

    } catch (error) {
      console.error('Error recreating user in Auth:', error);
      
      // Si el error es que el email ya está en uso, intentar obtener el UID del usuario existente
      if (error.code === 'auth/email-already-in-use') {
        console.log('⚠️ Email ya en uso (catch externo). Buscando usuario existente...');
        
        try {
          const { email } = solicitudData;
          const usuariosQuery = query(
            collection(db, 'usuarios'),
            where('email', '==', email),
            limit(1)
          );
          const usuariosSnapshot = await getDocs(usuariosQuery);
          
          if (!usuariosSnapshot.empty) {
            const usuarioDoc = usuariosSnapshot.docs[0];
            const usuarioData = usuarioDoc.data();
            const uidExistente = usuarioData.uid || usuarioDoc.id;
            
            console.log('✅ Usuario existente encontrado en catch externo, UID:', uidExistente);
            
            return {
              success: true,
              uid: uidExistente,
              email: email,
              usuarioYaExistia: true,
              message: 'El usuario ya existía en Firebase Auth. Se actualizó la información.'
            };
          } else {
            return {
              success: false,
              error: 'El email ya está registrado en Firebase Auth pero no se encontró en la base de datos. Contacta al administrador.',
              code: 'auth/email-already-in-use',
              emailExists: true
            };
          }
        } catch (searchError) {
          console.error('❌ Error buscando usuario existente en catch externo:', searchError);
          return {
            success: false,
            error: `El email ya está registrado en Firebase Auth. Error al buscar usuario: ${searchError.message}`,
            code: 'auth/email-already-in-use',
            emailExists: true
          };
        }
      }
      
      return {
        success: false,
        error: error.message,
        code: error.code
      };
    }
  }

  /**
   * Crear usuario de proveedor a partir de una solicitud aprobada
   */
  static async createProviderUser(solicitudData) {
    try {
      const { email_empresa, password_hash } = solicitudData;
      if (!email_empresa) {
        return { success: false, error: 'Email de empresa requerido', code: 'missing-email' };
      }

      // No intentar crear en Auth si el email ya está en uso (evita auth/email-already-in-use)
      const methods = await fetchSignInMethodsForEmail(auth, email_empresa);
      if (methods && methods.length > 0) {
        return {
          success: false,
          error: 'El email ya está registrado en la plataforma. Usa el flujo de activación para vincular la cuenta existente.',
          code: 'auth/email-already-in-use'
        };
      }

      // Extraer datos de la solicitud
      const {
        nombres_representante,
        apellidos_representante,
        nombre_empresa,
        rut_empresa,
        telefono_empresa,
        direccion_empresa,
        comuna,
        region,
        categorias_servicios,
        marcas_vehiculos,
        descripcion_negocio,
        horario_atencion
      } = solicitudData;

      // Decodificar la contraseña
      const password = atob(password_hash);

      // Crear usuario en Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, email_empresa, password);
      const user = userCredential.user;

      // Crear perfil de empresa en Firestore
      const empresaData = {
        uid: user.uid,
        email: user.email,
        
        // Información de la empresa
        nombre: nombre_empresa,
        rut: rut_empresa,
        telefono: telefono_empresa,
        direccion: direccion_empresa,
        comuna,
        region,
        descripcion: descripcion_negocio,
        horario_atencion,
        
        // Información del representante
        representante: {
          nombres: nombres_representante,
          apellidos: apellidos_representante,
          email: solicitudData.email_representante || email_empresa,
          telefono: solicitudData.telefono_representante || telefono_empresa,
          cargo: solicitudData.cargo_representante || 'Propietario'
        },
        
        // Servicios y capacidades
        categorias: categorias_servicios || [],
        marcas: Array.isArray(marcas_vehiculos) ? marcas_vehiculos : [],
        
        // Estado y configuración
        fecha_registro: new Date(),
        estado_validacion: 'activo',
        tipo_usuario: 'proveedor',
        tipoEmpresa: 'proveedor', // Siempre proveedor en MVP
        fecha_activacion: new Date(),
        verificado: true,
        
        // Configuraciones
        acepta_notificaciones: solicitudData.acepta_notificaciones !== false,
        visible_en_busqueda: true,
        acepta_servicios_emergencia: false,
        
        // Estadísticas iniciales
        total_servicios_completados: 0,
        calificacion_promedio: null,
        total_clientes_atendidos: 0,
        
        // Metadatos
        creado_desde_solicitud: true,
        solicitud_original_id: solicitudData.id || null,
        ip_registro: solicitudData.ip_registro || null,
        user_agent: solicitudData.user_agent || null
      };

      // Guardar en la colección empresas
      const docRef = await addDoc(collection(db, 'empresas'), empresaData);

      // Crear registro en la colección usuarios para asignar el rol
      const usuarioData = {
        uid: user.uid,
        email: user.email,
        rol: 'proveedor',
        nombre: nombre_empresa,
        empresaId: docRef.id,
        fechaCreacion: new Date(),
        activo: true
      };
      
      // Usar setDoc con el UID de Firebase Auth como ID del documento
      await setDoc(doc(db, 'usuarios', user.uid), usuarioData);

      return {
        success: true,
        user: user,
        profileId: docRef.id,
        uid: user.uid,
        email: user.email
      };

    } catch (error) {
      console.error('Error creating provider user:', error);
      
      let errorMessage = 'Error al crear el usuario proveedor';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'El email ya está registrado en el sistema';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'La contraseña es muy débil';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'El email no es válido';
      }

      return {
        success: false,
        error: errorMessage,
        code: error.code
      };
    }
  }

  /**
   * Verificar si un email ya existe en Authentication
   */
  static async checkEmailExists(email) {
    try {
      // No hay método directo para verificar email, intentamos crear un usuario temporal
      // Esto es solo una verificación, no crear realmente el usuario
      const methods = await fetchSignInMethodsForEmail(auth, email);
      return methods.length > 0;
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    }
  }
}

export default UserCreationService;
